import { DOCUMENT } from "@angular/common";
import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  Renderer2,
  ViewChild,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { CommonService } from "./_services/common.service";
import { ModalService } from "./_services/modal.service";
import { ApprovalWaitingComponent } from "./components/approval-waiting/approval-waiting.component";
import { NavigationEnd, Router } from "@angular/router";
import { AuthenticationService } from "./_services/authentication.service";
import { GetPlatformService } from "./_services/get-platform.service";
import { ThemeConfigurationService } from "./_services/theme-configuration.service";
import { LoginConfigService } from "./_services/login-config.service";
import { SEOConfigurationService } from "./_services/seo-configuration.service";
import { MetaTagsService } from "./_services/meta-tags.service";
import { catchError, of, Subscription } from "rxjs";

interface NavItem {
  label: string;
  icon: string;
  link?: string;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
})
export class AppComponent implements OnDestroy {
  // Reactive Collapsed State for Sidebar Animation
  isSidebarCollapsed: boolean = false;
  isSocialOpen: boolean = false;

  @ViewChild("socialDropdown") socialDropdown?: ElementRef<HTMLElement>;
  @ViewChild("socialTrigger") socialTrigger?: ElementRef<HTMLButtonElement>;
  private subscriptions = new Subscription();
  private profileRequested = false;
  showShell = false;
  faviconUrl = "./assets/icon/favicon.png";

  get supplierName(): string {
    return this.commonService.profileData?.companyName || "Supplier";
  }

  get brandName(): string {
    const key = this.commonService.domDirectionLTR
      ? "brand_name"
      : "brand_name_ar";
    return (
      this.seoConfigurationService.get(key) ||
      this.seoConfigurationService.get("brand_name") ||
      "Supplier"
    );
  }

  get profileName(): string {
    return this.commonService.profileData?.name || this.supplierName;
  }

  get profileInitials(): string {
    return this.profileName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();
  }

  get activeSocialLinks() {
    return this.seoConfigurationService.socialLinks();
  }

  // Structured Navigation Groups
  navGroups: NavGroup[] = [
    {
      title: "Workspace",
      items: [
        {
          label: "Dashboard",
          icon: "insert_chart",
          link: "/account-details/marketplace-dashboard",
        },
        {
          label: "Products Management",
          icon: "category",
          link: "/account-details/products-management",
        },
        {
          label: "Featured Products Management",
          icon: "stack_star",
          link: "/account-details/features-products-management",
        },
        {
          label: "Orders Management",
          icon: "orders",
          link: "/account-details/orders-management",
        },
        {
          label: "Product Reviews",
          icon: "rate_review",
          link: "/account-details/product-reviews",
        },
        {
          label: "Material Types",
          icon: "texture_add",
          link: "/account-details/material-types",
        },
        {
          label: "Price Groups",
          icon: "price_change",
          link: "/account-details/price-groups",
        },
        {
          label: "Vat Management",
          icon: "calculate",
          link: "/account-details/vat-config",
        },
      ],
    },
    {
      title: "Administration",
      items: [
        {
          label: "Profile",
          icon: "account_circle",
          link: "/account-details/my-profile",
        },
        {
          label: "Brand Management",
          icon: "branding_watermark",
          link: "/account-details/brand-management",
        },
        {
          label: "Settings",
          icon: "settings",
          link: "/account-details/settings",
        },
      ],
    },
  ];

  // Sidebar Toggle Logic
  toggleSidebar(): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
    this.isSocialOpen = false;
  }

  // Header Dropdown Toggle Logic
  toggleSocials(): void {
    this.isSocialOpen = !this.isSocialOpen;
  }

  openSocialLink(event: MouseEvent, url: string): void {
    event.preventDefault();
    // Open synchronously while the user gesture is active, before removing the anchor.
    this.document.defaultView?.open(url, "_blank", "noopener,noreferrer");
    this.isSocialOpen = false;
  }

  @HostListener("document:click", ["$event"])
  closeSocialsOutside(event: MouseEvent): void {
    if (
      this.isSocialOpen &&
      !this.socialDropdown?.nativeElement.contains(event.target as Node)
    ) {
      this.isSocialOpen = false;
    }
  }

  @HostListener("document:keydown.escape")
  closeSocialsWithEscape(): void {
    if (this.isSocialOpen) {
      this.isSocialOpen = false;
      this.socialTrigger?.nativeElement.focus();
    }
  }

  closeSocialsOnBlur(event: FocusEvent): void {
    if (
      !this.socialDropdown?.nativeElement.contains(event.relatedTarget as Node)
    )
      this.isSocialOpen = false;
  }

  onNavigation(link: string): void {
    this.isSocialOpen = false;
    if (this.document.defaultView?.matchMedia("(max-width: 760px)").matches)
      this.isSidebarCollapsed = true;
    if (link === "/account-details/orders-management")
      this.commonService.localStorageRemove("selectedCustomer");
  }

  private updateShell(url: string): void {
    this.showShell = /^\/account-details(?:\/|[?#]|$)/.test(url);
    this.isSocialOpen = false;
    if (
      this.showShell &&
      !this.profileRequested &&
      localStorage.getItem("accessToken")
    ) {
      this.profileRequested = true;
      this.commonService.getProfileData();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  constructor(
    @Inject(DOCUMENT) private document: any,
    private renderer2: Renderer2,
    private translate: TranslateService,
    public commonService: CommonService,
    private router: Router,
    private authService: AuthenticationService,
    private modalService: ModalService,
    public getPlatformService: GetPlatformService,
    private themeConfigurationService: ThemeConfigurationService,
    private loginConfigService: LoginConfigService,
    private seoConfigurationService: SEOConfigurationService,
    private metaTagsService: MetaTagsService,
  ) {
    this.isSidebarCollapsed =
      this.document.defaultView?.matchMedia("(max-width: 760px)").matches ??
      false;
    this.updateShell(this.router.url);
    this.subscriptions.add(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd)
          this.updateShell(event.urlAfterRedirects);
      }),
    );

    this.themeConfigurationService.ensureConfigurationLoaded().subscribe();
    this.loadFavIcon();
    this.seoConfigurationService.ensureConfigurationLoaded().subscribe(() => {
      this.metaTagsService.initializeMetaTags();
    });

    translate.addLangs(["en", "ar"]);
    if (localStorage.getItem("locale")) {
      const browserLang = localStorage.getItem("locale");
      translate.use(browserLang.match(/en|ar/) ? browserLang : "en");
    } else {
      localStorage.setItem("locale", "en");
      translate.setDefaultLang("en");
    }

    this.getPlatformService.getPlatformType();
    this.checkApproveStatus();
  }

  private loadFavIcon(): void {
    this.subscriptions.add(
      this.loginConfigService
        .getFavIconConfig()
        .pipe(catchError(() => of(null)))
        .subscribe((res) => {
          const url = res?.data?.find(
            (item: any) => item.image_type === "favicon",
          )?.image_url;
          if (!url) return;
          this.faviconUrl = url;

          const link: HTMLLinkElement | null =
            this.document.querySelector("link[rel*='icon']");
          if (link) {
            link.href = url;
          } else {
            const newLink = this.document.createElement("link");
            newLink.rel = "icon";
            newLink.href = url;
            this.document.head.appendChild(newLink);
          }
        }),
    );
  }

  changeLang(language: string) {
    localStorage.setItem("locale", language);
    this.translate.use(language);
  }

  approvalCheck() {
    let obj = {
      component: ApprovalWaitingComponent,
      backdropDismiss: true,
      canDismiss: true,
      cssClass: "approval-modal",
    };
    const modal = this.modalService.openModal(obj);

    modal.then(
      (value) => {
        this.logout();
      },
      (error) => {
        console.log(error);
      },
    );
  }

  checkApproveStatus() {
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken) {
      let companyData = localStorage.getItem("companyData");
      if (companyData == "true") {
        let isApproved = localStorage.getItem("isApproved");
        if (isApproved != "true") {
          this.approvalCheck();
        }
      } else {
        this.router.navigate(["/company-details"]);
      }
    }
  }

  logout() {
    const finishLogout = () => {
      const locale = localStorage.getItem("locale");
      localStorage.clear();
      if (locale) localStorage.setItem("locale", locale);
      this.commonService.accessToken = null;
      this.commonService.profileData = {};
      this.commonService.twoFaEnabled = false;
      this.commonService.setEvent("userchange", this.commonService.profileData);
      this.authService.loggedInStatus.next(false);
      this.profileRequested = false;
      this.isSocialOpen = false;
      this.router.navigate(["/login"], { replaceUrl: true });
    };
    this.authService
      .logout()
      .subscribe({ next: finishLogout, error: finishLogout });
  }
}
